import {
  Activity,
  Article,
  CreditCard,
  Garage,
  Package,
  ShoppingBag,
  Storefront,
  TrayArrowUp,
  Warehouse,
} from "@phosphor-icons/react"
import { Menu, MenuItem, Popover, Tooltip } from "@vesatogo/grass-core"
import { NavLink, Navigate, Outlet } from "react-router-dom"
import ProductionTimeline from "~/components/ProductionPlanning/ProductionTimeline"
import RequestTimeline from "~/components/RequestTimeline"
import { Permission } from "~/constants/Permissions"
import BankReconciliation from "~/icons/BankReconciliation"
import Payable from "~/icons/Payable"
import PattiDetail from "~/pages/d/Patti/[id]"
import PlantSettlement from "~/pages/d/bank-reconciliation"
import InvoiceSettlement from "~/pages/d/bank-reconciliation/invoice-settlement"
import Credit from "~/pages/d/credit"
import CreditDetails from "~/pages/d/credit/[id]"
import DemandManagement from "~/pages/d/demand-management"
import DemandDetails from "~/pages/d/demand-management/[id]"
import OfferManagement from "~/pages/d/offer-management"
import OfferDetails from "~/pages/d/offer-management/[id]"
import PayableSettlement from "~/pages/d/payable"
import AdvancePayable from "~/pages/d/payable/advance"
import NetPayable from "~/pages/d/payable/net"
import ProductionPlanning from "~/pages/d/production-planning"
import ProductionPlanningDetail from "~/pages/d/production-planning/[id]"
import { ProductionFinal } from "~/pages/d/production-planning/final"
import StockManagement from "~/pages/d/stock-management"
import StorageLocations from "~/pages/d/storage-locations"
import CreateStorageLocation from "~/pages/d/storage-locations/create"
import StorageLocationDetail from "~/pages/d/storage-locations/detail"
import TradeMetrics from "~/pages/d/trade-metrics"
import AllTradeRequests from "~/pages/d/trade-requests"
import TradeRequestDetail from "~/pages/d/trade-requests/[id]"
import TradeInvoices from "~/pages/d/trade-requests/invoices"
import TradeReceipts from "~/pages/d/trade-requests/receipts"
import ReceiptDetail from "~/pages/d/trade-requests/receipts/[id]"
import AllTrips from "~/pages/d/trade-requests/trip"
import TripDetail from "~/pages/d/trade-requests/trip/[id]"
import { TradeTemplates } from "~/pages/d/trade-templates"
import Uploader from "~/pages/d/uploader"
import UploaderTaskDetail from "~/pages/d/uploader/[id]"
import MandiList from "~/pages/mandi-listing"
import HishobPatti from "~/pages/mandi-listing/hishob-patti"
import PackingMaterial from "~/pages/packing-material"

function SidebarMenuItem({ link, name }: { link: string; name: string }) {
  return (
    <MenuItem className="p-0 [&>div]:m-0">
      <NavLink
        className={({ isActive }) => `p-2 block ${isActive && "bg-gray-200"}`}
        to={link}
        state={{ fromSidebar: true }}
      >
        {name}
      </NavLink>
    </MenuItem>
  )
}

export const dashboardRoutes: any[] = [
  {
    path: "trade-metrics",
    title: "Trade Metrics",
    icon: (
      <Tooltip content={"Trade Metrics"} placement="right">
        <Activity weight="fill" />
      </Tooltip>
    ),
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <TradeMetrics />,
      },
    ],
  },
  {
    path: "trade-requests",
    title: "Trade Requests",
    icon: (
      <Tooltip content={"Trade Requests"} placement="right">
        <ShoppingBag weight="fill" />
      </Tooltip>
    ),
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <AllTradeRequests />,
      },
      {
        path: ":id",
        element: <TradeRequestDetail />,
      },
      {
        path: "receipts",
        element: <TradeReceipts />,
      },
      {
        path: "invoices",
        element: <TradeInvoices />,
      },
      {
        path: "receipts/:id",
        element: <ReceiptDetail />,
      },
      {
        path: "trip",
        element: <AllTrips />,
      },
      {
        path: "trip/:id",
        element: <TripDetail />,
      },
    ],
  },
  {
    path: "trade-templates",
    title: "Trade Templates",
    permission: Permission.canViewExpenseTemplate,
    icon: (
      <Popover
        minimal
        arrow={false}
        className="p-0 border-t border-gray-200 ml-4 -mt-4"
        trigger="mouseover"
        interactive
        animation="fade"
        placement="right-start"
        content={
          <Menu className="max-h-52">
            <SidebarMenuItem link="trade-templates/expenses" name="Expenses" />
            <SidebarMenuItem
              link="trade-templates/seller"
              name="Supplier Terms"
            />
            <SidebarMenuItem link="trade-templates/buyer" name="Buyer Terms" />
          </Menu>
        }
      >
        <Article weight="fill" />
      </Popover>
    ),
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="expenses" />,
      },
      {
        path: "expenses",
        title: "Expenses Templates",
        element: <TradeTemplates type="expenses" />,
      },
      {
        path: "seller",
        title: "Supplier Terms Templates",
        element: <TradeTemplates type="seller" />,
      },
      {
        path: "buyer",
        title: "Buyer Terms Templates",
        element: <TradeTemplates type="buyer" />,
      },
    ],
  },
  {
    path: "mandi-listing",
    title: "Mandi Listing",
    icon: (
      <Tooltip content={"Mandi Listing"} placement="right">
        <Storefront weight="fill" />
      </Tooltip>
    ),
    permission: Permission.ViewSummaryDashboard,
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <MandiList />,
      },

      {
        path: "hishob-patti",
        element: <HishobPatti />,
      },
    ],
  },
  {
    path: "packing-material",
    title: "Packing Material",
    icon: (
      <Tooltip content="Packing Material" placement="right">
        <Package weight="fill" />
      </Tooltip>
    ),
    permission: Permission.canViewPackingMaterial,
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <PackingMaterial />,
      },
    ],
  },
  {
    path: "payable",
    title: "Payable Management",
    icon: (
      <Tooltip content="Payable Management" placement="right">
        <Payable weight="fill" />
      </Tooltip>
    ),
    permission: Permission.canViewPayableManagement,
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <PayableSettlement />,
      },
      {
        path: "advance",
        element: <AdvancePayable />,
      },
      {
        path: "net",
        element: <NetPayable />,
      },
    ],
  },
  {
    path: "bank-reconciliation",
    title: "Bank Reconciliation",
    icon: (
      <Tooltip content="Bank Reconciliation" placement="right">
        <BankReconciliation weight="fill" />
      </Tooltip>
    ),
    permission: Permission.canViewReceivableManagement,
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <PlantSettlement />,
      },
      {
        path: "invoice-settlement",
        element: <InvoiceSettlement />,
      },
    ],
  },
  {
    path: "uploader",
    title: "Uploader",
    icon: (
      <Tooltip content="Uploader" placement="right">
        <TrayArrowUp weight="fill" />
      </Tooltip>
    ),
    permission: Permission.canViewUploaderManager,
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <Uploader />,
      },
      {
        path: ":id",
        element: <UploaderTaskDetail />,
      },
    ],
  },
  {
    path: "credit",
    title: "Credit Management",
    icon: (
      <Tooltip content="Credit Management" placement="right">
        <CreditCard weight="fill" />
      </Tooltip>
    ),
    permission: Permission.canViewCreditManagement,
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <Credit />,
      },
      {
        path: ":id",
        element: <CreditDetails />,
      },
    ],
  },
  {
    path: "patti",
    title: "Patti Management",
    hide: true,
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        path: ":id",
        element: <PattiDetail />,
        title: "Patti",
      },
    ],
  },

  {
    path: "stock-management",
    title: "Stock Management",
    icon: (
      <Tooltip content="Stock Management" placement="right">
        <Warehouse weight="fill" />
      </Tooltip>
    ),
    permission: Permission.canViewStockManagement,
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <StockManagement />,
      },
      {
        path: "offer",
        element: <OfferManagement />,
      },
      {
        path: "offer/:id",
        element: <OfferDetails />,
      },

      {
        path: "demand",
        element: (
          <>
            <RequestTimeline />
            <Outlet />
          </>
        ),
        children: [
          {
            index: true,
            element: <DemandManagement />,
          },
          {
            path: ":id",
            element: <DemandDetails />,
          },
        ],
      },
    ],
  },

  {
    path: "storage-locations",
    icon: (
      <Tooltip content="Storage Location" placement="right">
        <Garage weight="fill" />
      </Tooltip>
    ),
    title: "Storage Location",
    permission: Permission.CanViewStorageLocations,
    element: (
      <>
        <RequestTimeline />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <StorageLocations />,
      },
      {
        path: "new",
        element: <CreateStorageLocation />,
      },
      {
        path: ":id/edit",
        element: <CreateStorageLocation />,
      },
      {
        path: ":id",
        element: <StorageLocationDetail />,
      },
    ],
  },
  {
    path: "production-planning",
    title: "Production Management",
    icon: (
      <Tooltip content="Production Management" placement="right">
        <Warehouse weight="fill" />
      </Tooltip>
    ),
    permission: Permission.CanViewProductionManagement,
    element: (
      <>
        <ProductionTimeline isProductionTrade />
        <Outlet />
      </>
    ),
    children: [
      {
        index: true,
        element: <ProductionPlanning />,
      },
      {
        path: ":id",
        element: <ProductionPlanningDetail />,
      },
      {
        path: "final",
        element: <ProductionFinal />,
      },
    ],
  },
]
